import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Alert, Badge, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useSearchParams, useLocation } from 'react-router-dom';

import { RootState } from '../store';
import { DataTable } from '../components/common/DataTable/DataTable';
import { ColumnConfig } from '../components/common/DataTable/types';
import { FilterBar, FilterDefinition } from '../components/common/FilterControls';
import Select from '../components/common/Select';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { useAllUsers } from '../hooks/useAllUsers';
import ApplicantDocPreview from '../components/ApplicantDocPreview';
import { getEmails, useGetUsersId, usePostEmailsRetry } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { EmailMessage, ApplicantDocument, EmailLogSummary, GetEmails200, GetEmailsParams, User } from '../api/generated/model';

interface UserOption { value: string; label: string; }

const FETCH_LIMIT = 50;

const fmtDate = (s?: string) => (s ? new Date(s).toLocaleString() : '');
const fmtDateTime = (s?: string) => {
  if (!s) return '';
  const d = new Date(s);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const statusVariant: Record<string, string> = { received: 'info', classified: 'success', error: 'danger' };
const verdictVariant: Record<string, string> = {
  resume_cover: 'success', inline_cover: 'success', linked: 'info', duplicate: 'secondary',
  uninteresting: 'light', internal_sender: 'light', error: 'danger',
};

const filterDefs: FilterDefinition[] = [
  { type: 'text', field: 'search', label: 'Search', icon: 'bi-search', placeholder: 'Subject, from, applicant…' },
  { type: 'text', field: 'message_id', label: 'Message ID', icon: 'bi-hash', placeholder: 'Gmail message-id…' },
  {
    type: 'select', field: 'status', label: 'Status', icon: 'bi-flag',
    options: [
      { value: 'received', label: 'Received' },
      { value: 'classified', label: 'Classified' },
      { value: 'error', label: 'Error' },
    ],
  },
  {
    type: 'select', field: 'verdict', label: 'Verdict', icon: 'bi-tag',
    options: [
      { value: 'resume_cover', label: 'Resume/Cover' },
      { value: 'inline_cover', label: 'Inline Cover' },
      { value: 'linked', label: 'Linked (new address)' },
      { value: 'duplicate', label: 'Duplicate' },
      { value: 'uninteresting', label: 'Uninteresting' },
      { value: 'internal_sender', label: 'Internal Sender' },
      { value: 'error', label: 'Error' },
    ],
  },
];

const EmailLogTab: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });

  // Sort + filter state lives in the URL query params (shareable, survives
  // reload). All sorting/filtering is server-side (libs/cursor).
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const sortField = searchParams.get('sort_field') || 'message_date';
  const sortDir: 'asc' | 'desc' = searchParams.get('sort_direction') === 'asc' ? 'asc' : 'desc';
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || '';
  const verdict = searchParams.get('verdict') || '';
  const messageId = searchParams.get('message_id') || '';
  const filters = { search, status, verdict, message_id: messageId };
  const [viewerDoc, setViewerDoc] = useState<ApplicantDocument | null>(null);

  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Always reflect the active sort in the URL — populate the defaults on first
  // load (filters stay absent until the user sets them).
  useEffect(() => {
    if (location.pathname !== '/applicants/email-log') return; // don't rewrite URL from another tab
    if (!searchParams.get('sort_field') || !searchParams.get('sort_direction')) {
      updateParams({ sort_field: sortField, sort_direction: sortDir });
    }
  }, [location.pathname, searchParams, updateParams, sortField, sortDir]);

  // Admin-only "whose log" picker (mirrors the team-commissions selector). The
  // default option is your own log; admins can pick any active user. The choice
  // rides in the recruiter_user_id query param; the backend pins non-admins to
  // self regardless.
  const isAdmin = user?.role === 'admin';
  const { data: usersData } = useAllUsers();
  const recruiterUserId = searchParams.get('recruiter_user_id') || '';
  const userOptions = useMemo<UserOption[]>(() => {
    const opts: UserOption[] = [{ value: '', label: 'My log' }];
    (usersData || [])
      .filter((u: User) => u.active !== false && u.id && u.id !== user?.id)
      .sort((a: User, b: User) => `${a.first_name ?? ''} ${a.last_name ?? ''}`.localeCompare(`${b.first_name ?? ''} ${b.last_name ?? ''}`))
      .forEach((u: User) => opts.push({ value: u.id!, label: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email || u.id! }));
    return opts;
  }, [usersData, user?.id]);
  const selectedUserOption = userOptions.find((o) => o.value === recruiterUserId) || userOptions[0];
  const [showFilterBar, setShowFilterBar] = useState(false);
  const activeFilterCount = [search, status, verdict, messageId].filter(Boolean).length;

  // Resume/Cover cells mirror the Applicants page: the cell text is the time the
  // doc was received (applied_at) and clicking opens the inline viewer.
  const docLink = (doc?: ApplicantDocument) =>
    doc ? (
      <a
        href="#"
        className="text-nowrap"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setViewerDoc(doc); }}
      >
        {fmtDateTime(doc.applied_at)}
      </a>
    ) : null;

  const buildQueryParams = useCallback(
    (cursor: string | undefined) => {
      if (cursor) return { cursor, limit: FETCH_LIMIT };
      return {
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(verdict ? { verdict } : {}),
        ...(messageId ? { message_id: messageId } : {}),
        ...(recruiterUserId ? { recruiter_user_id: recruiterUserId } : {}),
        sort_field: sortField,
        sort_direction: sortDir,
        limit: FETCH_LIMIT,
      };
    },
    [search, status, verdict, messageId, recruiterUserId, sortField, sortDir]
  );

  const { allItems, totalCount, isLoading, hasMore, handleLoadMore, reset } = usePaginatedQuery<EmailMessage, GetEmails200, EmailLogSummary>({
    queryKey: 'email-log',
    buildQueryParams,
    queryFn: (params) => getEmails(params as GetEmailsParams),
    getRowKey: (m) => m.id,
  });

  // "Retry errored" re-queues terminal error rows for the current scope (own log,
  // or the admin-picked user) — they move error→received and the classifier
  // reprocesses them. reset() re-fetches page 1 so the status change shows.
  const retryMutation = usePostEmailsRetry();
  const [retryMsg, setRetryMsg] = useState<{ variant: string; text: string } | null>(null);
  const handleRetry = async () => {
    setRetryMsg(null);
    try {
      const res = await retryMutation.mutateAsync({
        params: recruiterUserId ? { recruiter_user_id: recruiterUserId } : undefined,
      });
      const n = res?.retried ?? 0;
      reset();
      setRetryMsg(
        n > 0
          ? { variant: 'success', text: `Re-queued ${n} errored message${n === 1 ? '' : 's'} for reprocessing.` }
          : { variant: 'secondary', text: 'No errored messages to retry.' }
      );
    } catch (e: any) {
      setRetryMsg({ variant: 'danger', text: e?.response?.data?.message || e?.response?.data?.error || 'Retry failed.' });
    }
  };

  // Columns mirror the AppScript log sheet, in the same order, then Status (a
  // dashboard-pipeline field not in the sheet) at the end. All server-sortable.
  const columns: ColumnConfig<EmailMessage>[] = [
    { key: 'processed_at', label: 'Processed At', sortable: true, width: 160, renderCell: (m) => fmtDate(m.processed_at) },
    { key: 'message_id', label: 'Message ID', sortable: true, width: 220, renderCell: (m) => <span className="text-truncate d-block">{m.message_id}</span> },
    { key: 'message_date', label: 'Message Date', sortable: true, width: 160, renderCell: (m) => fmtDate(m.message_date) },
    { key: 'from', label: 'From', sortable: true, width: 220, renderCell: (m) => m.from_name || m.from_address || '' },
    { key: 'subject', label: 'Subject', sortable: true, width: 280, renderCell: (m) => <span className="text-truncate d-block">{m.subject || ''}</span> },
    { key: 'verdict', label: 'Verdict', sortable: true, width: 140, renderCell: (m) => (m.verdict ? <Badge bg={verdictVariant[m.verdict] || 'secondary'} text={verdictVariant[m.verdict] === 'light' ? 'dark' : undefined}>{m.verdict}</Badge> : '') },
    { key: 'action', label: 'Action', sortable: true, width: 160, renderCell: (m) => <span className="text-truncate d-block">{m.action || ''}</span> },
    { key: 'reason', label: 'Reason', sortable: true, width: 320, renderCell: (m) => <span className="text-wrap" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{m.reason || ''}</span> },
    { key: 'applicant_email', label: 'Applicant', sortable: true, width: 220, renderCell: (m) => m.applicant_email || '' },
    { key: 'resume', label: 'Resume', sortable: true, width: 150, renderCell: (m) => docLink(m.resume_document) },
    { key: 'cover', label: 'Cover', sortable: true, width: 150, renderCell: (m) => docLink(m.cover_document) },
    { key: 'status', label: 'Status', sortable: true, width: 110, renderCell: (m) => <Badge bg={statusVariant[m.status] || 'secondary'}>{m.status}</Badge> },
  ];

  return (
      <>
      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
        <Card.Header className="bg-primary text-white flex-shrink-0">
          <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
                <span className="text-primary fw-semibold">
                  <i className="bi bi-envelope-check me-1"></i>
                  Email Log
                </span>
                {isAdmin && (
                  <div style={{ minWidth: 220 }}>
                    <Select<UserOption>
                      options={userOptions}
                      value={selectedUserOption}
                      onChange={(opt) => updateParams({ recruiter_user_id: opt?.value || undefined })}
                      isClearable={recruiterUserId !== ''}
                    />
                  </div>
                )}
                <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>
                <Button
                  variant={activeFilterCount > 0 ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setShowFilterBar(!showFilterBar)}
                >
                  <i className="bi bi-funnel me-1"></i>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="badge bg-light text-primary ms-1">{activeFilterCount}</span>
                  )}
                </Button>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="bg-header-control rounded px-2 py-1">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleRetry}
                  disabled={retryMutation.isPending}
                  title="Re-queue errored messages for reprocessing"
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  {retryMutation.isPending ? 'Retrying…' : 'Retry errored'}
                </Button>
              </div>
              <div className="rounded px-3 py-1">
                <span className="text-white">
                  {allItems.length > 0 ? (
                    <>{totalCount ?? allItems.length} total</>
                  ) : (
                    'No email log rows'
                  )}
                </span>
              </div>
            </div>
          </div>
          {showFilterBar && (
            <FilterBar
              filters={filters}
              filterDefs={filterDefs}
              onFilterChange={(f, v) => updateParams({ [f]: v || undefined })}
              onClearFilters={() => updateParams({ search: undefined, status: undefined, verdict: undefined, message_id: undefined })}
            />
          )}
        </Card.Header>
        {retryMsg && (
          <Alert
            variant={retryMsg.variant}
            className="m-2 py-2 d-flex justify-content-between align-items-center"
          >
            <span>{retryMsg.text}</span>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => setRetryMsg(null)} />
          </Alert>
        )}
        <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
          <DataTable<EmailMessage>
            data={allItems}
            columns={columns}
            getRowKey={(m) => m.id}
            sortConfig={{ field: sortField, direction: sortDir }}
            onSortChange={(field, dir) => updateParams({ sort_field: field, sort_direction: dir })}
            pagination={{ hasMore, limit: FETCH_LIMIT }}
            onLoadMore={handleLoadMore}
            isLoading={isLoading}
            preferencesKey="email-log"
            userId={user?.id}
            userPreferences={userData?.preferences}
            maxHeight="100%"
            enableStickyHeader
            enableColumnReorder
            enableColumnResize
            enableColumnVisibility
            emptyState={<Alert variant="secondary" className="m-3">No email log rows.</Alert>}
          />
        </Card.Body>
      </Card>
      <ApplicantDocPreview doc={viewerDoc} onClose={() => setViewerDoc(null)} />
      </>
  );
};

export default EmailLogTab;
